import { getContractLinkLimit, getContractLinkList } from '@modusign/openapi-client-template';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { Label } from '@app/features/label/types';
import workspaceService from '@app/libs/workspaceService';
import BlobFile from '@app/store/documents/BlobFile';
import SampleFile from '@app/store/documents/SampleFile';
import UploadFile from '@app/store/documents/UploadFile';
import { modalClose, modalOpen } from '@app/store/modal/actions';
import { documentUpload } from '@app/store/transport-layer/upload/document/upload';

import ContractLinkLimits from './types/ContractLinkLimits';

export const contractLinkCreateLoadPdfRequest = createAction<
  {
    fileId: string;
    title: string;
    url: string;
  },
  'contractLinkCreate/loadPdfRequest'
>('contractLinkCreate/loadPdfRequest');

export const contractLinkCreateLoadPdfSuccess = createAction<
  {
    fileId: string;
    pdfArrayBuffer: ArrayBuffer;
    title: string;
    url: string;
  },
  'contractLinkCreate/loadPdfSuccess'
>('contractLinkCreate/loadPdfSuccess');

export const contractLinkCreateLoadPdfFailure = createAction<
  { error: AxiosError },
  'contractLinkCreate/loadPdfFailure'
>('contractLinkCreate/loadPdfFailure');

export const contractLinkCreateMaxSigningPerParticipantChange = createAction<{
  maxSigningPerParticipant: number | null;
}>('contractLinkCreate/changeMaxSigningPerParticipant');

export const confirmThatFieldCannotBeModified = createAction(
  'contractLinkCreate/confirmThatFieldCannotBeModified'
);

export const contractLinkCreateSetLabels = createAction<Label[]>('contractLinkCreate/setLabels');

export type ContractLinkCreateAction =
  | ReturnType<typeof contractLinkCreateLoadPdfFailure>
  | ReturnType<typeof contractLinkCreateLoadPdfRequest>
  | ReturnType<typeof contractLinkCreateLoadPdfSuccess>;

export const contractLinkCreateFileUpload = createAsyncThunk(
  'contractLinkCreate/fileUpload',
  async (payload: File | SampleFile | UploadFile | BlobFile, { dispatch }) => {
    try {
      const responseData = await documentUpload(payload);

      const { fileId, title, url } = responseData;
      dispatch(contractLinkCreateLoadPdfRequest({ fileId, title, url }));

      // 파일 업로드 모달 닫기
      dispatch(modalClose());

      dispatch(
        modalOpen('CONTRACT_LINK_START_CONFIRM_MODAL', {
          initializeDocumentRenderTree: true,
        })
      );

      return responseData;
    } catch (error) {
      dispatch(modalOpen('FILE_UPLOAD_ERROR_MODAL', { error }));
      throw error;
    }
  }
);

export const checkContractLinkLimits = createAsyncThunk<
  ContractLinkLimits,
  undefined,
  { rejectValue: Error }
>('contractLinkCreate/checkContractLinkLimits', async () => {
  try {
    const workspaceId = workspaceService.getWorkspaceId();
    const limits = await getContractLinkLimit(workspaceId);
    const { count: activeLinkCount } = await getContractLinkList(workspaceId, {
      status: 'ACTIVE',
    });

    return Promise.resolve<ContractLinkLimits>({
      activeLinkCount,
      concurrentActiveLinksLimit: limits.concurrentActiveLinks,
      isLimitExceeded: limits.concurrentActiveLinks <= activeLinkCount,
      maxSigningPerSignerLimit: limits.maxSigningPerSigner,
    });
  } catch (error) {
    return Promise.reject(error);
  }
});
